import React from 'react';
import PropTypes from 'prop-types';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';
import cx from 'classnames';
import Button from 'components/common/Button';
import ProgressBar from 'components/Credit24/Core/ProgressBar';
import SubmitButton from 'components/Credit24/LoanApplication/LoanFormFooter/SubmitButton';
import {
  STEP_ALMOST_THERE,
  STEP_INFORMATION,
  STEP_SACC_LOADING,
  STEP_EMAIL_VERIFICATION,
} from 'consts/loan/steps';

const maxStepsFitSmallestMobileScreen = 11;

const propTypes = {
  loanFormStepBack: PropTypes.func,
  skipNordigenStep: PropTypes.func,
  loan: PropTypes.object.isRequired,
};

const defaultProps = {
  loanFormStepBack: () => {
  },
};

const LoanFormFooter = ({ loan, loanFormStepBack, skipNordigenStep }) => {
  const {
    currentStep, steps, isContentLoading, isLoanSubmitting, canChangeLimit,
  } = loan;

  const currentStepObject = steps[currentStep];
  const currentStepType = currentStepObject.type;
  let previousStepType;
  if (currentStep > 1) {
    previousStepType = steps[(currentStep - 1)].type;
  }

  if (COUNTRY === 'LV' && INSTANTOR_ENABLED) {
    if (currentStepType === 'INSTANTOR') {
      return (
        <footer className="loan-form__footer loan-form__footer--center">
          <button
            className="loan-form__footer-btn loan-form__footer-btn--next btn"
          >
            <FormattedMessage id="navigation.continue_without_bank" />
          </button>
        </footer>
      );
    }

    if (VARIANT_B) {
      if (currentStepType === 'SUBMIT') {
        if (isContentLoading) {
          return null;
        }

        return (
          <footer
            className="loan-form__footer loan-form__footer--center loan-form__footer--vertical loan-form__footer--mobile-vertical"
          >
            <p>
              <button
                className="loan-form__footer-btn loan-form__footer-btn--next btn btn--wide btn--mobile-wide"
              >
                <FormattedMessage id="submit.variant_b.apply" />
              </button>
            </p>
            <div className="txt-small desk-one-half">
              <FormattedHTMLMessage id="submit.variant_b.apply.subtext" />
            </div>
          </footer>
        );
      }
    }
  }

  const lvRemainingSteps = INSTANTOR_ENABLED ? 4 : 2;

  let isBackBtnDisabled;
  let isNextBtnDisabled;

  let hasButtons;

  switch (true) {
    case currentStepType === 'SCORING_STATUS_AU':
    case currentStepType === 'CREDIT_SENSE':
    case currentStepType === 'IMPERSONATION_AU':
    case COUNTRY === 'AU' && isContentLoading:
    case currentStepType === STEP_ALMOST_THERE.type:
    case currentStepType === STEP_INFORMATION.type:
    case currentStepType === STEP_SACC_LOADING.type:
    case currentStepType === STEP_EMAIL_VERIFICATION.type:
      hasButtons = false;
      break;
    default:
      hasButtons = true;
  }

  switch (true) {
    case isLoanSubmitting || isContentLoading || loan.isLoadingMMPbyMonth:
    case currentStepType === 'PRODUCT' && !loan.selectedProduct:
    case currentStepType === 'NORDIGEN' && !loan.selectedNordigenBank:
    case COUNTRY === 'LV' && currentStep + lvRemainingSteps === steps.length:
      isNextBtnDisabled = true;
      break;
    default:
      isNextBtnDisabled = false;
  }

  switch (true) {
    case isLoanSubmitting || isContentLoading:
    case currentStepType === 'LOGIN':
    case currentStepType === 'CUSTOMER' || currentStepType === 'CUSTOMER_FI' || currentStepType === 'CUSTOMER_LV':
    case currentStepType === 'CUSTOMER_LT':
    case currentStepType === 'ADDRESS_LT' && steps.findIndex((item) => item.type === 'CUSTOMER_LT') === -1:
    case currentStepType === 'ADDRESS_AU':
    case currentStepType === 'ADDRESS' && COUNTRY === 'EE':
    case currentStepType === 'INCOME_FI' && COUNTRY === 'FI':
    case currentStepType === 'PAYMENT_DAY' && (COUNTRY === 'FI' || COUNTRY === 'EE'):
    case currentStepType === 'PRODUCT' || currentStepType === 'SUBMIT':
    case currentStepType === 'VERIFY' || currentStepType === 'INSTANTOR':
    case currentStepType === 'ONE_CENT' && !INSTANTOR_ENABLED:
    case currentStepType === 'ONE_CENT' && INSTANTOR_ENABLED
      && loan.verifyOptions === 'instantor':
    case currentStepType === 'ONE_CENT' && INSTANTOR_ENABLED && COUNTRY === 'LV'
      && currentStepObject.userInfo && currentStepObject.userInfo.isBackBtnDisabled:
    case currentStepType === 'CONTRACT_DETAILS':
    case currentStepType === 'CREDIT_LINE':
    case currentStepType === 'EDUCATION_LT':
    case currentStepType === 'ADDRESS_LT':
    case currentStepType === 'NATIONALITY_LT':
    case currentStepType === 'REGISTRIES_LT':
    case currentStepType === 'LOAN_TYPE_LT':
    case currentStepType === 'INCOME_LT' && previousStepType !== 'LOAN_TYPE_LT':
      isBackBtnDisabled = true;
      break;
    default:
      isBackBtnDisabled = false;
  }

  const nextBtnClasses = cx({
    'loan-form__footer-btn': true,
    'loan-form__footer-btn--next': true,
    'btn--wide': currentStepType === 'PRODUCT',
    invisible: COUNTRY === 'LT' && currentStepType === 'CONFIRMATION' && loan.hiddenConfirmationButton,
  });

  let nextBtnTextKey;
  switch (true) {
    case COUNTRY === 'LV' && currentStepType === 'ONE_CENT':
      nextBtnTextKey = 'navigation.done';
      break;
    case COUNTRY === 'LT' && currentStepType === 'PRODUCT':
      nextBtnTextKey = 'offer.submit.takeMoney';
      break;
    case COUNTRY === 'LT' && currentStepType === 'EDUCATION_LT':
      nextBtnTextKey = 'navigation.next.education';
      break;
    case COUNTRY === 'AU' && INCREASE_APP && currentStepType === 'PRODUCT':
      nextBtnTextKey = 'offer.upgrade.submit';
      break;
    case COUNTRY === 'AU' && currentStepType === 'PRODUCT':
      nextBtnTextKey = 'offer.submit';
      break;
    case COUNTRY === 'LV' && currentStepType === 'PRODUCT' && canChangeLimit:
      nextBtnTextKey = 'offer.variant_b.changing.submit';
      break;
    default:
      nextBtnTextKey = 'navigation.next';
  }

  const SubmitBtn = (
    <SubmitButton
      isLoanSubmitting={isLoanSubmitting}
      isContentLoading={isContentLoading}
      legalDocument={loan.legalDocument}
    />
  );
  const NextBtn = (
    <Button
      className={nextBtnClasses}
      disabled={isNextBtnDisabled}
      isLoading={isLoanSubmitting}
    >
      <FormattedMessage id={nextBtnTextKey} />
    </Button>
  );

  let PrimaryButton = NextBtn;
  if ((COUNTRY === 'LV' && currentStep + lvRemainingSteps === steps.length)
    || currentStepType === 'SUBMIT') {
    PrimaryButton = SubmitBtn;
  }

  let isBackBtnHidden;
  switch (currentStepType) {
    case 'PRODUCT':
    case 'SUBMIT':
    case 'LOAN_PURPOSE':
    case 'CONSENTS_AU':
      isBackBtnHidden = true;
      break;
    default:
      isBackBtnHidden = false;
  }

  if ((COUNTRY === 'EE' || COUNTRY === 'LV') && currentStepType === 'LOAN_PURPOSE') {
    isBackBtnHidden = false;
  }

  let isBackBtnInvisible;

  switch (currentStepType) {
    case 'EDUCATION_LT':
    case 'ADDRESS_LT':
    case 'NATIONALITY_LT':
    case 'REGISTRIES_LT':
    case 'LOAN_TYPE_LT':
      isBackBtnInvisible = true;
      break;
    default:
      isBackBtnInvisible = false;
  }

  if (currentStepType === 'INCOME_LT' && previousStepType !== 'LOAN_TYPE_LT') {
    isBackBtnInvisible = true;
  }

  const backBtnClassNames = cx({
    'loan-form__footer-btn loan-form__footer-btn--back': true,
    'invisible hide-mobile': isBackBtnInvisible,
  });

  const hasDetails = COUNTRY === 'AU' && currentStepType === 'SUBMIT';
  const isProgressBarVisible = !currentStepObject.hideFromProgressBar;
  const footerClasses = cx({
    'loan-form__footer': true,
    'loan-form__footer--vertical': hasDetails,
    'loan-form__footer--center': (isProgressBarVisible && !hasButtons) || (!isProgressBarVisible && isBackBtnHidden),
  });

  const visibleSteps = steps.filter((step) => !step.hideFromProgressBar);

  if (COUNTRY === 'EE' && currentStepType === 'NORDIGEN') {
    return (
      <footer className={footerClasses}>
        {hasButtons && PrimaryButton}
        {hasButtons && !isBackBtnHidden
          && (
            <Button
              type="button"
              btnType="skip-step"
              className="loan-form__footer-btn "
              onClick={skipNordigenStep}
            >
              <FormattedMessage id="nordigen.back" />
            </Button>
          )}
      </footer>
    );
  }

  return (
    <footer className={footerClasses}>
      {hasButtons && !isBackBtnHidden
        && (
          <Button
            type="button"
            btnType="secondary"
            className={backBtnClassNames}
            onClick={loanFormStepBack}
            disabled={isBackBtnDisabled}
          >
            <FormattedMessage id="navigation.back" />
          </Button>
        )}
      {isProgressBarVisible
        && (
          <ProgressBar
            visibleSteps={visibleSteps}
            progressIndex={visibleSteps.indexOf(currentStepObject)}
            type={visibleSteps.length > maxStepsFitSmallestMobileScreen ? 'small-mobile' : null}
            hidesStepTitle={COUNTRY !== 'AU'}
          />
        )}
      {hasButtons && PrimaryButton}
    </footer>
  );
};

LoanFormFooter.propTypes = propTypes;

LoanFormFooter.defaultProps = defaultProps;

export default LoanFormFooter;
